/**
 * Multi-line (wiring) diagram of a board, generated from its single-line diagram.
 *
 * Layout: incoming terminals X0 (L or L1-L2-L3, N, PE) → main switch → main RCD → bus bars;
 * each circuit is a 2-pole breaker (single-phase, fed from its phase and N) or a 3/4-pole
 * breaker (three-phase), fed from the buses or from an RCD group's own buses, wired to the
 * outgoing terminal strip X1 with the circuit letter, description and cable underneath. PE runs
 * on its own bar. Single-phase circuits on a three-phase board go on the phase set in their
 * `phase` property, or are spread over L1, L2, L3 in turn.
 *
 * A board wider than an A3 sheet continues on further sheets: every bus bar ends on an
 * off-page connector and resumes on the next sheet.
 *
 * The generated sheets remember a signature of the single-line data they were made from, so the
 * app can tell when they are out of date and regenerate them. Regenerating replaces their
 * content: hand edits on generated sheets are not kept (the UI says so).
 */
import {
  applyTransaction,
  newId,
  removeSheetOps,
  type Element,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type Sheet,
  type WireEnd,
  tr,
} from '@elec/domain';
import {
  analyse,
  boardCircuits,
  boardPhases,
  circuitPoles,
  effectiveRcd,
  multiLineSignature,
  type Analysis,
  type CircuitInfo,
} from '@elec/analysis';

export { multiLineSignature, staleMultiLines } from '@elec/analysis';

const X0 = 25;
const PAGE_RIGHT = 420 - 15; // A3 landscape, inside the frame

class B {
  ops: Op[] = [];
  sheetId: Id;
  private terminalNo = new Map<string, number>();
  constructor(sheetId: Id) {
    this.sheetId = sheetId;
  }
  el(
    symbolId: string,
    x: number,
    y: number,
    tag: string,
    props: Record<string, PropValue> = {},
    mirrored = false,
  ): Id {
    const id = newId();
    const value: Element = { id, sheetId: this.sheetId, symbolId, x, y, rotation: 0, mirrored, tag, props };
    this.ops.push({ op: 'add', coll: 'elements', value });
    return id;
  }
  terminal(strip: string, conductorX: number, y: number, number?: string): Id {
    const n = number ?? String((this.terminalNo.get(strip) ?? 0) + 1);
    if (!number) this.terminalNo.set(strip, Number(n));
    return this.el('iec.terminal', conductorX - 2.5, y, strip, { number: n });
  }
  wire(from: WireEnd, to: WireEnd, label?: string) {
    this.ops.push({
      op: 'add',
      coll: 'wires',
      value: { id: newId(), sheetId: this.sheetId, from, to, via: [], ...(label ? { label } : {}) },
    });
  }
  text(x: number, y: number, text: string, size = 2.5) {
    this.el('annot.text', x, y, '', { text, size });
  }
}
const port = (elementId: Id, portId: string): WireEnd => ({ kind: 'port', elementId, portId });
const pt = (x: number, y: number): WireEnd => ({ kind: 'point', x, y });

const deviceProps = (el: Element): Record<string, PropValue> => {
  const p: Record<string, PropValue> = {};
  for (const k of ['ratingA', 'curve', 'breakingKa', 'sensitivityMa', 'rcdType'])
    if (el.props[k] !== undefined) p[k] = el.props[k]!;
  return p;
};

/** The sheets generated for a board, in part order. */
export function multiLineSheets(
  project: Project,
  fromSheetId: Id,
  kind: 'multi-line' | 'board-layout' = 'multi-line',
): Sheet[] {
  return Object.values(project.sheets)
    .filter((s) => s.source?.kind === kind && s.source.fromSheetId === fromSheetId)
    .sort((a, b) => (a.source!.part ?? 1) - (b.source!.part ?? 1));
}

/**
 * Generate (or regenerate in place) the multi-line sheet(s) for a single-line sheet.
 * Returns the ops and the id of the first multi-line sheet.
 */
export function generateMultiLineOps(
  project: Project,
  fromSheetId: Id,
  analysis: Analysis = analyse(project),
): { ops: Op[]; sheetId: Id } {
  const from = project.sheets[fromSheetId];
  if (!from) return { ops: [], sheetId: '' };
  const existing = multiLineSheets(project, fromSheetId);
  const ops: Op[] = [];
  const signature = multiLineSignature(analysis, fromSheetId);
  const L = project.meta.locale;
  const baseName = `${tr(L, 'Multifilaire', 'Multi-line', 'Meerdraads', 'Mehrliniendiagramm')} — ${from.name}`;
  const baseOrder = existing[0]?.order ?? from.order + 0.5;
  const sheetIds: Id[] = [];
  /** Open part n (1-based): reuse an existing generated sheet, cleared, or add one. */
  const openPart = (n: number): Id => {
    const old = existing[n - 1];
    const id = old?.id ?? newId();
    const source = { kind: 'multi-line' as const, fromSheetId, signature, ...(n > 1 ? { part: n } : {}) };
    const name = n > 1 ? `${baseName} (${n})` : baseName;
    if (old) {
      ops.push(...removeSheetOps(project, id).filter((o) => !(o.op === 'remove' && o.coll === 'sheets')));
      ops.push({
        op: 'update',
        coll: 'sheets',
        id,
        patch: { source, name, paper: 'A3', orientation: 'landscape' },
      });
    } else
      ops.push({
        op: 'add',
        coll: 'sheets',
        value: {
          id,
          name,
          kind: 'multi-line',
          paper: 'A3',
          orientation: 'landscape',
          order: baseOrder + (n - 1) / 100,
          source,
        },
      });
    sheetIds.push(id);
    return id;
  };

  const b = new B(openPart(1));
  const circuits = boardCircuits(analysis, fromSheetId);
  const devices = Object.values(project.elements).filter((e) => e.sheetId === fromSheetId);
  const phases = boardPhases(project, analysis, fromSheetId);
  const three = phases.three;
  const P = three ? ['L1', 'L2', 'L3'] : ['L'];
  const C = [...P, 'N'];
  const n = C.length;
  const dx = (i: number) => 5 + 10 * i; // conductor i of a device placed at x
  const Y = (() => {
    const bus0 = 97.5;
    const groupRcd = bus0 + 5 * n + 2.5;
    const sub0 = groupRcd + 27.5;
    const breaker = sub0 + 5 * n + 2.5;
    const pe = breaker + 35;
    const outT = pe + 10;
    return { inT: 25, main: 40, rcd: 70, bus0, groupRcd, sub0, breaker, pe, outT, text: outT + 12 };
  })();
  const busY = (k: number) => Y.bus0 + 5 * k;
  const subY = (k: number) => Y.sub0 + 5 * k;
  const main = devices.find((e) => e.symbolId === 'iec.switch-disconnector');
  const origin = circuits.map((c) => c.rcds.at(-1)).find(Boolean);

  // ---- incoming side
  const xin = C.map((c, i) => b.terminal('X0', X0 + dx(i), Y.inT, c));
  const xpe = b.terminal('X0', X0 - 5, Y.inT, 'PE');
  let outs: WireEnd[] = xin.map((t) => port(t, 'b'));
  const stage = (symbolId: string, y: number, el: Element) => {
    const d = b.el(symbolId, X0, y, el.tag, deviceProps(el));
    outs.forEach((o, i) => b.wire(o, port(d, `in${i + 1}`)));
    outs = C.map((_, i) => port(d, `out${i + 1}`));
  };
  if (main) stage(`iec.switch-disconnector-${n}p`, Y.main, main);
  if (origin) stage(`iec.rcd-${n}p`, Y.rcd, origin);
  outs.forEach((o, i) => b.wire(o, pt(X0 + dx(i), busY(i))));
  b.text(
    X0 - 10,
    Y.inT - 4,
    `${tr(L, 'Arrivée', 'Supply', 'Voeding', 'Einspeisung')} ${project.meta.supplyVoltage}`,
    2.5,
  );

  // ---- bus bars, possibly over several sheets
  const peY = Y.pe;
  let page = 1;
  let busStart = X0 + 5; // x where this sheet's main buses start (conductor 0)
  let peStart = X0 - 5;
  let x = X0 + 40;
  let lastFeed = X0 + 40; // rightmost column feeding from the main buses on this sheet
  let group: { start: number; last: number } | null = null;
  const group0 = String(fromSheetId);
  const offpage = (xx: number, yy: number, link: string, incoming: boolean): WireEnd => {
    const id = b.el('iec.offpage', incoming ? xx - 10 : xx, yy - 2.5, '', { link, group: group0 }, incoming);
    return port(id, 'a');
  };
  const closeMain = (end: number, label: boolean) => {
    C.forEach((c, i) =>
      b.wire(pt(busStart + 10 * i, busY(i)), pt(end + 10 * i, busY(i)), label ? c : undefined),
    );
  };
  const closeGroup = (end: number) => {
    if (!group) return;
    C.forEach((_, i) => {
      if (end > group!.start) b.wire(pt(group!.start + 10 * i, subY(i)), pt(end + 10 * i, subY(i)));
    });
  };
  let mainNeededLater = true;
  let mainOnPage = true;
  const breakPage = () => {
    const end = PAGE_RIGHT - 25;
    // Main buses (when still needed) and PE end on off-page connectors; so does the open RCD
    // group's bus.
    const continueMain = !group || mainNeededLater;
    C.forEach((c, i) => {
      if (!mainOnPage) return;
      if (continueMain)
        b.wire(
          pt(busStart + 10 * i, busY(i)),
          offpage(end + 10 * i, busY(i), `${c}·${page}`, false),
          i === 0 ? c : undefined,
        );
      else
        b.wire(pt(busStart + 10 * i, busY(i)), pt(lastFeed + 5 + 10 * i, busY(i)), i === 0 ? c : undefined);
    });
    b.wire(pt(peStart, peY), offpage(end + 30, peY, `PE·${page}`, false), 'PE');
    if (group)
      C.forEach((c, i) =>
        b.wire(pt(group!.start + 10 * i, subY(i)), offpage(end + 10 * i, subY(i), `${c}·${page}g`, false)),
      );
    page++;
    b.sheetId = openPart(page);
    const startX = 25;
    mainOnPage = mainOnPage && continueMain;
    if (mainOnPage)
      C.forEach((c, i) =>
        b.wire(offpage(startX - 5 + 10 * i, busY(i), `${c}·${page - 1}`, true), pt(startX + 10 * i, busY(i))),
      );
    b.wire(offpage(startX - 5, peY, `PE·${page - 1}`, true), pt(startX, peY));
    if (group) {
      C.forEach((c, i) =>
        b.wire(
          offpage(startX - 5 + 10 * i, subY(i), `${c}·${page - 1}g`, true),
          pt(startX + 10 * i, subY(i)),
        ),
      );
      group = { start: startX, last: startX };
    }
    busStart = startX;
    peStart = startX;
    x = startX + 15;
    lastFeed = startX;
  };

  const column = (c: CircuitInfo, fromGroup: boolean) => {
    const poles = circuitPoles(c);
    const width = poles === 2 ? 35 : poles * 10 + 15;
    if (x + width > PAGE_RIGHT - 30) breakPage();
    const cx = x;
    const feedY = (k: number) => (fromGroup ? subY(k) : busY(k));
    const q = b.el(`iec.breaker-${poles}p`, cx, Y.breaker, c.device.tag, deviceProps(c.device));
    let conductors: Array<{ name: string; bus: number }>;
    if (poles === 2) {
      // Same phase as the balance check (R-CALC-06): its `phase`, else L1, L2, L3 in turn.
      const k = Math.max(0, P.indexOf(phases.of.get(c.device.id)?.[0] ?? ''));
      conductors = [
        { name: P[k]!, bus: k },
        { name: 'N', bus: n - 1 },
      ];
    } else conductors = (poles === 4 ? C : P).map((name) => ({ name, bus: C.indexOf(name) }));
    conductors.forEach((cd, i) => {
      b.wire(pt(cx + dx(i), feedY(cd.bus)), port(q, `in${i + 1}`));
      const t = b.terminal('X1', cx + dx(i), Y.outT);
      b.wire(port(q, `out${i + 1}`), port(t, 'a'));
    });
    const tpe = b.terminal('X1', cx + dx(conductors.length), Y.outT);
    b.wire(pt(cx + dx(conductors.length), peY), port(tpe, 'a'));
    const phaseNote = three && poles === 2 ? ` · ${conductors[0]!.name}` : '';
    const desc = [c.code, c.circuit?.label].filter(Boolean).join(' · ') + phaseNote;
    if (desc) b.text(cx, Y.text, desc, 2.5);
    const cable =
      c.cable.sectionMm2 > 0
        ? `${c.cable.type} ${c.cable.cores}G${String(c.cable.sectionMm2).replace('.', ',')}`.trim()
        : '';
    if (cable) b.text(cx, Y.text + 4, cable, 2.2);
    if (c.device.symbolId === 'iec.rcbo')
      b.text(cx, Y.text + 8, `DD ${effectiveRcd(c)?.sensitivityMa ?? ''} mA`, 2.2);
    if (fromGroup) group!.last = cx;
    else lastFeed = cx;
    x += width;
  };

  // Columns: circuits directly under the origin RCD first, then one group per further RCD.
  const groups = new Map<Id, CircuitInfo[]>();
  const direct: CircuitInfo[] = [];
  for (const c of circuits) {
    const nearest = c.rcds[0];
    if (!nearest || nearest.id === origin?.id) direct.push(c);
    else groups.set(nearest.id, [...(groups.get(nearest.id) ?? []), c]);
  }
  for (const c of direct) column(c, false);
  let gi = 0;
  for (const [rcdId, list] of groups) {
    mainNeededLater = ++gi < groups.size;
    const rcd = project.elements[rcdId]!;
    if (x + 10 * n + 5 > PAGE_RIGHT - 30) breakPage();
    const gx = x;
    const r = b.el(`iec.rcd-${n}p`, gx, Y.groupRcd, rcd.tag, deviceProps(rcd));
    C.forEach((_, i) => {
      b.wire(pt(gx + dx(i), busY(i)), port(r, `in${i + 1}`));
      b.wire(port(r, `out${i + 1}`), pt(gx + dx(i), subY(i)));
    });
    lastFeed = gx;
    group = { start: gx + 5, last: gx };
    for (const c of list) column(c, true);
    closeGroup(group.last + 5);
    group = null;
  }
  // Close the buses on the last sheet (labelled with their potentials).
  const busEnd = Math.max(lastFeed + 5, busStart + 10);
  if (mainOnPage) closeMain(busEnd, page === 1);
  if (page === 1) b.wire(port(xpe, 'b'), pt(X0 - 5, peY));
  b.wire(pt(peStart, peY), pt(Math.max(x - 5, peStart + 10), peY), page === 1 ? 'PE' : undefined);

  // Parts no longer needed.
  for (const old of existing.slice(page)) ops.push(...removeSheetOps(project, old.id));
  ops.push(...b.ops);
  // Sanity: the ops must apply cleanly.
  applyTransaction(project, { label: '', ops });
  return { ops, sheetId: sheetIds[0]! };
}
